// apps/client/src/pages/leads/edm/components/AbTestPanel.tsx
//
// Plan-v3 core Slice E1 (edm_tracking): an A/B subject-line test. A share of
// the list (10-50 %) gets subject A or B; after the wait, the subject with
// the higher unique-open rate goes to everyone else. Opens are approximate,
// so the winner is too. The server validates every field and refuses an A/B
// test on a campaign without tracking (ab_requires_tracking).

import React from 'react';
import { FlaskConical } from 'lucide-react';
import { EDM_UI, mergeLintError } from '../../../../utils/edm';
import type { EdmAbTest } from '../../../../hooks/useEdmCampaigns';

/** The preset options plus the stored value when it is not a preset (so the select never misreports it). */
const withValue = (options: readonly number[], v: number): number[] =>
  options.includes(v) ? [...options] : [...options, v].sort((a, b) => a - b);

export const AB_PERCENT_OPTIONS = [10, 20, 30, 40, 50] as const;
export const AB_HOURS_OPTIONS = [1, 2, 4, 6, 12, 24] as const;
export const DEFAULT_AB_TEST: EdmAbTest = { subjectB: '', testPercent: 20, decideAfterHours: 4 };

/** Lint for the A/B settings, or null (client mirror of the server ranges). */
export function abTestLint(ab: EdmAbTest | null, subjectA: string): string | null {
  if (ab === null) return null;
  if (!ab.subjectB.trim()) return 'Add subject line B, or switch the test off.';
  if (ab.subjectB.trim() === subjectA.trim()) return 'Subject line B must differ from subject line A.';
  if (!(ab.testPercent >= 10 && ab.testPercent <= 50)) return 'The test group must be 10% to 50% of the list.';
  if (!(ab.decideAfterHours >= 1 && ab.decideAfterHours <= 24)) return 'Pick the winner after 1 to 24 hours.';
  return mergeLintError(ab.subjectB);
}

const AbTestPanel: React.FC<{
  value: EdmAbTest | null;
  subjectA: string;
  onChange: (v: EdmAbTest | null) => void;
  disabled?: boolean;
}> = ({ value, subjectA, onChange, disabled }) => {
  const on = value !== null;
  const lint = abTestLint(value, subjectA);
  return (
    <div className="space-y-3 rounded-lg border border-gray-700 bg-gray-900/40 p-3" data-testid="edm-ab-test">
      <label className="flex items-start gap-3 text-sm text-gray-200">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4"
          checked={on}
          disabled={disabled}
          onChange={(e) => onChange(e.target.checked ? { ...DEFAULT_AB_TEST } : null)}
        />
        <span className="inline-flex items-center gap-1.5">
          <FlaskConical className="h-4 w-4 text-blue-400" /> Test a second subject line (A/B)
        </span>
      </label>
      {on && value && (
        <div className="space-y-3">
          <div>
            <label htmlFor="edm-subject-b" className={EDM_UI.label}>
              Subject line B
            </label>
            <input
              id="edm-subject-b"
              className={EDM_UI.input}
              maxLength={200}
              value={value.subjectB}
              disabled={disabled}
              onChange={(e) => onChange({ ...value, subjectB: e.target.value })}
            />
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label htmlFor="edm-ab-percent" className={EDM_UI.label}>
                Test group
              </label>
              <select
                id="edm-ab-percent"
                className={EDM_UI.input}
                value={String(value.testPercent)}
                disabled={disabled}
                onChange={(e) => onChange({ ...value, testPercent: Number(e.target.value) })}
              >
                {withValue(AB_PERCENT_OPTIONS, value.testPercent).map((p) => (
                  <option key={p} value={p}>
                    {p}% of the list (half get A, half get B)
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="edm-ab-hours" className={EDM_UI.label}>
                Pick the winner after
              </label>
              <select
                id="edm-ab-hours"
                className={EDM_UI.input}
                value={String(value.decideAfterHours)}
                disabled={disabled}
                onChange={(e) => onChange({ ...value, decideAfterHours: Number(e.target.value) })}
              >
                {withValue(AB_HOURS_OPTIONS, value.decideAfterHours).map((h) => (
                  <option key={h} value={h}>
                    {h} {h === 1 ? 'hour' : 'hours'}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <p className="text-xs text-gray-400">
            The subject with the higher open rate goes to the rest of the list. Opens are approximate (some mail apps open
            every email automatically, others block images), so treat the winner as a guide. Each subject needs at least 20
            sends to win; otherwise subject A is used.
          </p>
          {lint && !disabled && <p className="text-xs text-red-400">{lint}</p>}
        </div>
      )}
    </div>
  );
};

export default AbTestPanel;
